/**
 * Blog content loader (LL-0095). Reads every Markdown file in content/blog/ at
 * build time via Vite's import.meta.glob (eager + ?raw), parses the frontmatter
 * the Site Margin Studio producer emits, and exposes a typed post list to the
 * /blog index + /blog/:slug pages and to scripts/prerender.mjs (which parses the
 * same frontmatter independently, in plain Node, so keep the contract simple).
 *
 * Frontmatter contract (producer draft-blog.mjs buildMdx + the publisher):
 *   title, description, slug, date, keywords (array), canonical, clusterTag,
 *   image (optional hero image path).
 * Each value is JSON-encoded by the producer, so a JSON.parse-first parser is
 * the most faithful reader; we fall back to a raw (quote-stripped) string for
 * hand-authored posts.
 */

import { asClusterTag, type ClusterTag } from "./clusterTags";
import { articleLd, serializeLd } from "./jsonLd";

export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string; // ISO date (YYYY-MM-DD); "" if unknown (sorts last)
  /** Last real content edit (YYYY-MM-DD); equals date until a real edit. */
  dateModified: string;
  keywords: string[];
  canonical: string;
  clusterTag: ClusterTag | null;
  /** Optional hero image path, e.g. "/blog-images/x.jpg". */
  image: string | null;
  body: string; // Markdown after the frontmatter block
  readingMinutes: number;
}

const SITE = "https://www.letsloop.app";

/** Rewrites a canonical still pointing at our own /blog path to /news. */
function legacyBlogToNews(url: string): string {
  return url.startsWith(`${SITE}/blog/`)
    ? url.replace(`${SITE}/blog/`, `${SITE}/news/`)
    : url;
}

// Eager raw import of every markdown file under content/blog. Keyed by path.
const files = import.meta.glob("../content/blog/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

/** Parse one frontmatter scalar: JSON first (producer output), else raw string. */
function parseValue(raw: string): unknown {
  const trimmed = raw.trim();
  try {
    return JSON.parse(trimmed);
  } catch {
    // Hand-authored value: strip a single pair of wrapping quotes if present.
    return trimmed.replace(/^['"]|['"]$/g, "");
  }
}

function parseFrontmatter(src: string): {
  meta: Record<string, unknown>;
  body: string;
} {
  const match = /^﻿?---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(src);
  if (!match) return { meta: {}, body: src.trim() };
  const meta: Record<string, unknown> = {};
  for (const line of match[1].split(/\r?\n/)) {
    const kv = /^([A-Za-z0-9_-]+):\s*(.*)$/.exec(line);
    if (kv) meta[kv[1]] = parseValue(kv[2]);
  }
  return { meta, body: match[2].trim() };
}

function fileSlug(path: string): string {
  return path
    .split("/")
    .pop()!
    .replace(/\.mdx?$/, "");
}

function toPost(path: string, src: string): BlogPost {
  const { meta, body } = parseFrontmatter(src);
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  const slug = str(meta.slug) || fileSlug(path);
  const words = body.split(/\s+/).filter(Boolean).length;
  const keywords = Array.isArray(meta.keywords)
    ? (meta.keywords as unknown[]).map(String)
    : [];
  return {
    slug,
    title: str(meta.title) || slug,
    description: str(meta.description),
    date: str(meta.date),
    dateModified: str(meta.dateModified) || str(meta.date),
    keywords,
    // Post frontmatter canonicals were rewritten to /news at source
    // (2026-08-03, LL-0015 Wave 1), but keep this guard: it rewrites OUR OWN
    // legacy /blog URLs so a thirteenth post pasted from an old template can
    // never make the Article schema's mainEntityOfPage point at a 301 while
    // <link rel=canonical> says /news. A canonical pointing anywhere else is
    // left alone: that is a deliberate syndication target, not a stale path.
    canonical: legacyBlogToNews(str(meta.canonical) || `${SITE}/news/${slug}`),
    // Validated against the five-cluster taxonomy: an off-vocabulary value
    // becomes null rather than polluting the PostHog super-property with
    // free-text (twelve posts had eleven distinct ad-hoc tags before this).
    clusterTag: asClusterTag(str(meta.clusterTag)),
    image: str(meta.image) || null,
    body,
    readingMinutes: Math.max(1, Math.round(words / 200)),
  };
}

/** All posts, newest first (undated posts sort last). */
export const posts: BlogPost[] = Object.entries(files)
  .map(([path, src]) => toPost(path, src))
  .sort((a, b) => (b.date || "").localeCompare(a.date || ""));

export function getPost(slug: string): BlogPost | undefined {
  return posts.find((p) => p.slug === slug);
}

/**
 * Schema.org Article JSON-LD for a post (built here, not trusted from
 * frontmatter). Shape lives in lib/jsonLd.ts (typed via schema-dts);
 * serializeLd escapes `<` so a `</script>` sequence in producer-supplied
 * text can never break out of the inline script tag.
 */
export function articleJsonLd(post: BlogPost): string {
  return serializeLd(articleLd(post));
}

/** Human date, e.g. "5 June 2026". Returns "" for undated posts. */
export function formatDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
